/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/db/index.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                         |
 * |---|------------------------|-------------------|------------------|--------------------------------------|
 * | 1 | 2026-06-18 (Phase 3)   | Claude Sonnet 4.6 | Phase 3 DB layer | Created — Dexie database + helpers   |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/components/TestLibrary.tsx
 * - apps/studio/src/components/ExecutionHistory.tsx
 * - apps/studio/src/App.tsx
 *
 * EXPORTS COVERED
 * ---------------
 * db               — singleton Dexie instance (IndexedDB "mimiq-studio" v1)
 * saveTestCase()   — upsert: create new or overwrite existing TestCase
 * saveRun()        — write Run + RunStep[] atomically; update TestCase.lastRun*
 * deleteTestCase() — remove TestCase + all its Runs + RunSteps
 * deleteSuite()    — remove Suite + all child TestCases recursively
 * getRootSuites()  — returns top-level Suites (parentId === undefined)
 *
 * CALLER → CALLEE MAP
 * --------------------
 * TestLibrary   → db.suites, db.testCases (via useLiveQuery)
 *               → saveTestCase(), deleteSuite(), deleteTestCase()
 * ExecutionHistory → db.runs, db.runSteps (via useLiveQuery)
 * App.tsx        → saveRun() after replay
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. DB name "mimiq-studio", version 1. Bump version + add migration when schema changes.
 * 2. Compound indexes: runSteps has [runId+stepIndex] for ordered step fetching.
 * 3. saveRun() runs in a transaction so Run + RunSteps are atomic.
 *    It also back-patches TestCase.lastRunId + lastRunStatus in the same transaction.
 * 4. The db singleton is exported directly — components import it and use
 *    useLiveQuery() from dexie-react-hooks for reactive queries.
 *
 * =============================================================================
 */

import Dexie, { type Table } from "dexie";
import { generateId } from "@mimiq/ir";
import type { Suite, TestCase, Run, RunStep, RunStatus, SharedFlow, SharedFlowFolder, RunGroup } from "./schema.js";
import type { ReplayStepResult } from "../device/replayer.js";

// ── Dexie database class ──────────────────────────────────────────────────────

class MimiqStudioDB extends Dexie {
  suites!: Table<Suite, string>;
  testCases!: Table<TestCase, string>;
  runs!: Table<Run, string>;
  runSteps!: Table<RunStep, string>;
  sharedFlows!: Table<SharedFlow, string>;
  sharedFlowFolders!: Table<SharedFlowFolder, string>;
  runGroups!: Table<RunGroup, string>;

  constructor() {
    super("mimiq-studio");
    this.version(1).stores({
      suites:    "id, parentId, name, createdAt",
      testCases: "id, suiteId, name, createdAt, lastRunStatus",
      runs:      "id, testCaseId, startedAt, status",
      runSteps:  "id, runId, [runId+stepIndex]",
    });
    this.version(2).stores({
      suites:    "id, parentId, name, createdAt",
      testCases: "id, suiteId, name, createdAt, lastRunStatus",
      runs:      "id, testCaseId, startedAt, status",
      runSteps:  "id, runId, [runId+stepIndex]",
    });
    // v3: adds sharedFlows table for the Function Library
    this.version(3).stores({
      suites:       "id, parentId, name, createdAt",
      testCases:    "id, suiteId, name, createdAt, lastRunStatus",
      runs:         "id, testCaseId, startedAt, status",
      runSteps:     "id, runId, [runId+stepIndex]",
      sharedFlows:  "id, name, createdAt",
    });
    // v4: adds sharedFlowFolders table + folderId index on sharedFlows
    this.version(4).stores({
      suites:             "id, parentId, name, createdAt",
      testCases:          "id, suiteId, name, createdAt, lastRunStatus",
      runs:               "id, testCaseId, startedAt, status",
      runSteps:           "id, runId, [runId+stepIndex]",
      sharedFlows:        "id, folderId, name, createdAt",
      sharedFlowFolders:  "id, parentId, name, createdAt",
    });
    // v5: adds runGroups table + groupId index on runs
    this.version(5).stores({
      suites:             "id, parentId, name, createdAt",
      testCases:          "id, suiteId, name, createdAt, lastRunStatus",
      runs:               "id, testCaseId, startedAt, status, groupId",
      runSteps:           "id, runId, [runId+stepIndex]",
      sharedFlows:        "id, folderId, name, createdAt",
      sharedFlowFolders:  "id, parentId, name, createdAt",
      runGroups:          "id, name, createdAt",
    });
  }
}

export type { RunGroup } from "./schema.js";

export const db = new MimiqStudioDB();

// ── saveTestCase ──────────────────────────────────────────────────────────────

export interface SaveTestCaseOpts {
  id?: string;       // provide to update; omit to create
  suiteId: string;
  name: string;
  tags?: string[];
  ir: import("@mimiq/ir").TestIR;
}

export async function saveTestCase(opts: SaveTestCaseOpts): Promise<TestCase> {
  const now = Date.now();
  const existing = opts.id ? await db.testCases.get(opts.id) : undefined;

  const tc: TestCase = {
    id: opts.id ?? generateId(),
    suiteId: opts.suiteId,
    name: opts.name,
    tags: opts.tags ?? [],
    ir: opts.ir,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    lastRunId: existing?.lastRunId,
    lastRunStatus: existing?.lastRunStatus,
  };

  await db.testCases.put(tc);
  return tc;
}

// ── SharedFlow CRUD ───────────────────────────────────────────────────────────

export type { SharedFlow, SharedFlowFolder } from "./schema.js";

export async function saveSharedFlow(
  opts: Partial<SharedFlow> & { name: string },
  existingId?: string,
): Promise<SharedFlow> {
  const now = Date.now();
  const existing = existingId ? await db.sharedFlows.get(existingId) : undefined;
  const sf: SharedFlow = {
    id: existingId ?? generateId(),
    folderId: opts.folderId,   // ← was missing — caused folder placement to never save
    name: opts.name,
    description: opts.description,
    tags: opts.tags ?? [],
    steps: opts.steps ?? [],
    parameters: opts.parameters ?? [],
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
  await db.sharedFlows.put(sf);
  return sf;
}

export async function deleteSharedFlow(id: string): Promise<void> {
  await db.sharedFlows.delete(id);
}

export async function duplicateSharedFlow(id: string): Promise<SharedFlow> {
  const original = await db.sharedFlows.get(id);
  if (!original) throw new Error(`SharedFlow ${id} not found`);
  return saveSharedFlow({ ...original, name: `${original.name} (copy)` });
}

// ── SharedFlowFolder CRUD ─────────────────────────────────────────────────────

export async function saveSharedFlowFolder(
  opts: { name: string; parentId?: string },
  existingId?: string,
): Promise<SharedFlowFolder> {
  const now = Date.now();
  const existing = existingId ? await db.sharedFlowFolders.get(existingId) : undefined;
  const folder: SharedFlowFolder = {
    id: existingId ?? generateId(),
    parentId: opts.parentId,
    name: opts.name,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
  await db.sharedFlowFolders.put(folder);
  return folder;
}

export async function deleteSharedFlowFolder(id: string): Promise<void> {
  // Move all functions in this folder to root
  const children = await db.sharedFlows.where("folderId").equals(id).toArray();
  await db.transaction("rw", [db.sharedFlowFolders, db.sharedFlows], async () => {
    for (const fn of children) {
      await db.sharedFlows.update(fn.id, { folderId: undefined });
    }
    // Also delete child folders recursively
    await deleteSharedFlowFolderRecursive(id);
  });
}

async function deleteSharedFlowFolderRecursive(id: string): Promise<void> {
  const children = await db.sharedFlowFolders.where("parentId").equals(id).toArray();
  for (const child of children) {
    await deleteSharedFlowFolderRecursive(child.id);
  }
  await db.sharedFlowFolders.delete(id);
}

// ── RunGroup CRUD ─────────────────────────────────────────────────────────────

export async function saveRunGroup(opts: { name: string; description?: string; color?: string }, existingId?: string): Promise<RunGroup> {
  const now = Date.now();
  const existing = existingId ? await db.runGroups.get(existingId) : undefined;
  const g: RunGroup = { id: existingId ?? generateId(), name: opts.name, description: opts.description, color: opts.color, createdAt: existing?.createdAt ?? now };
  await db.runGroups.put(g);
  return g;
}

export async function deleteRunGroup(groupId: string, deleteRuns: boolean): Promise<void> {
  await db.transaction("rw", [db.runGroups, db.runs, db.runSteps], async () => {
    if (deleteRuns) {
      const runs = await db.runs.where("groupId").equals(groupId).toArray();
      for (const r of runs) await db.runSteps.where("runId").equals(r.id).delete();
      await db.runs.where("groupId").equals(groupId).delete();
    } else {
      // Move runs to ungrouped
      await db.runs.where("groupId").equals(groupId).modify({ groupId: undefined });
    }
    await db.runGroups.delete(groupId);
  });
}

export async function moveRunsToGroup(runIds: string[], groupId: string | undefined): Promise<void> {
  await db.runs.where("id").anyOf(runIds).modify({ groupId });
}

// ── Run deletion ──────────────────────────────────────────────────────────────

export async function deleteRun(runId: string): Promise<void> {
  await db.transaction("rw", [db.runs, db.runSteps], async () => {
    await db.runSteps.where("runId").equals(runId).delete();
    await db.runs.delete(runId);
  });
}

export async function deleteRuns(runIds: string[]): Promise<void> {
  await db.transaction("rw", [db.runs, db.runSteps], async () => {
    for (const id of runIds) {
      await db.runSteps.where("runId").equals(id).delete();
    }
    await db.runs.where("id").anyOf(runIds).delete();
  });
}

// ── saveRun ───────────────────────────────────────────────────────────────────

export interface SaveRunOpts {
  runId?: string;           // pre-generated so OPFS screenshot paths can reference it
  testCaseId: string;
  testCaseName: string;
  startedAt: number;
  finishedAt: number;
  deviceModel: string;
  deviceSerial: string;
  androidVersion: string;
  stepResults: ReplayStepResult[];
  steps: readonly import("@mimiq/ir").IRStep[];
}

export async function saveRun(opts: SaveRunOpts): Promise<Run> {
  const passedCount = opts.stepResults.filter((r) => r.status === "ok").length;
  const failedCount = opts.stepResults.filter((r) => r.status !== "ok").length;
  const healCount = opts.stepResults.filter((r) => r.healHint !== undefined).length;

  const status: RunStatus =
    failedCount === 0 ? "passed"
    : healCount > 0 && failedCount === 0 ? "passed_healed"
    : "failed";

  // Snapshot the test case's current tags so the report reflects what tags
  // the test had when it ran (tags might change after the run).
  const snapshotTags = (await db.testCases.get(opts.testCaseId))?.tags ?? [];

  const run: Run = {
    id: opts.runId ?? generateId(),
    testCaseId: opts.testCaseId,
    testCaseName: opts.testCaseName,
    tags: snapshotTags,
    startedAt: opts.startedAt,
    finishedAt: opts.finishedAt,
    status,
    deviceModel: opts.deviceModel,
    deviceSerial: opts.deviceSerial,
    androidVersion: opts.androidVersion,
    stepCount: opts.stepResults.length,
    passedCount,
    failedCount,
    healCount,
  };

  const runSteps: RunStep[] = opts.stepResults.map((r) => ({
    id: generateId(),
    runId: run.id,
    stepIndex: r.stepIndex,
    stepId: r.stepId,
    stepType: opts.steps[r.stepIndex]?.type ?? "unknown",
    status: r.status === "ok" ? "ok"
          : r.status === "not_found" ? "not_found"
          : "error",
    matchedBy: r.matchedBy,
    healHint: r.healHint,
    x: r.x,
    y: r.y,
    detail: r.detail,
    screenshotRef: r.screenshotRef,
    a11yDump: r.a11yDump,
  }));

  await db.transaction("rw", [db.runs, db.runSteps, db.testCases], async () => {
    await db.runs.add(run);
    await db.runSteps.bulkAdd(runSteps);
    // Back-patch the TestCase with the latest run status.
    // Cast to any: Dexie's UpdateSpec<T> infers deep key paths which fails on
    // recursive IR types (IfStep.then/else, RepeatStep.steps, etc.)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (db.testCases.update as any)(opts.testCaseId, {
      lastRunId: run.id,
      lastRunStatus: status,
      updatedAt: Date.now(),
    });
  });

  return run;
}

// ── deleteTestCase ────────────────────────────────────────────────────────────

export async function deleteTestCase(id: string): Promise<void> {
  await db.transaction("rw", [db.testCases, db.runs, db.runSteps], async () => {
    const runIds = (await db.runs.where("testCaseId").equals(id).toArray()).map((r) => r.id);
    if (runIds.length > 0) {
      await db.runSteps.where("runId").anyOf(runIds).delete();
      await db.runs.where("testCaseId").equals(id).delete();
    }
    await db.testCases.delete(id);
  });
}

// ── deleteSuite ───────────────────────────────────────────────────────────────

export async function deleteSuite(id: string): Promise<void> {
  // Collect all descendant suite IDs recursively, then delete all their tests.
  const allSuiteIds: string[] = [id];
  async function collectChildren(parentId: string) {
    const children = await db.suites.where("parentId").equals(parentId).toArray();
    for (const child of children) {
      allSuiteIds.push(child.id);
      await collectChildren(child.id);
    }
  }
  await collectChildren(id);

  const testIds = (
    await db.testCases.where("suiteId").anyOf(allSuiteIds).toArray()
  ).map((tc) => tc.id);

  await db.transaction("rw", [db.suites, db.testCases, db.runs, db.runSteps], async () => {
    if (testIds.length > 0) {
      const runIds = (await db.runs.where("testCaseId").anyOf(testIds).toArray()).map((r) => r.id);
      if (runIds.length > 0) {
        await db.runSteps.where("runId").anyOf(runIds).delete();
        await db.runs.where("testCaseId").anyOf(testIds).delete();
      }
      await db.testCases.where("suiteId").anyOf(allSuiteIds).delete();
    }
    await db.suites.where("id").anyOf(allSuiteIds).delete();
  });
}

// ── createSuite ───────────────────────────────────────────────────────────────

export async function createSuite(name: string, parentId?: string): Promise<Suite> {
  const now = Date.now();
  const suite: Suite = {
    id: generateId(),
    parentId,
    name,
    createdAt: now,
    updatedAt: now,
  };
  await db.suites.add(suite);
  return suite;
}

// ── renameSuite ───────────────────────────────────────────────────────────────

export async function renameSuite(id: string, name: string): Promise<void> {
  await db.suites.update(id, { name, updatedAt: Date.now() });
}

// ── moveTestCase ──────────────────────────────────────────────────────────────

/** Move a single test case to a different suite. */
export async function moveTestCase(id: string, newSuiteId: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await (db.testCases.update as any)(id, { suiteId: newSuiteId, updatedAt: Date.now() });
}

// ── moveTestCases ─────────────────────────────────────────────────────────────

/** Bulk-move multiple test cases to the same target suite. */
export async function moveTestCases(ids: string[], newSuiteId: string): Promise<void> {
  const now = Date.now();
  await db.transaction("rw", db.testCases, async () => {
    for (const id of ids) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (db.testCases.update as any)(id, { suiteId: newSuiteId, updatedAt: now });
    }
  });
}

// ── duplicateTestCase ─────────────────────────────────────────────────────────

/**
 * Clone a test case. The copy lands in the same suite by default, or in
 * newSuiteId if provided. Name is prefixed with "Copy of ".
 */
export async function duplicateTestCase(id: string, newSuiteId?: string): Promise<TestCase> {
  const original = await db.testCases.get(id);
  if (!original) throw new Error(`TestCase ${id} not found`);
  return saveTestCase({
    suiteId: newSuiteId ?? original.suiteId,
    name: `Copy of ${original.name}`,
    tags: [...original.tags],
    ir: { ...original.ir },
  });
}

